import { useState, useCallback, useRef } from "react";
import { deleteSchedulerEvents } from "repositories/scheduler-repository";
import { handleError, handleSuccess } from "utils";
import {
  buildDeleteSchedulerEventsPayloads,
  getDeleteItemCounts,
} from "./eventsWorkspaceDelete.utils";

/**
 * Bulk Delete Selected flow for Event Workspace (all calendar views).
 * @param {{
 *   t: Function,
 *   selectedEvents: Record<string, {
 *     id: number,
 *     date: string,
 *     userId?: number|null,
 *     isShift?: boolean,
 *     isUnassigned?: boolean,
 *   }>,
 *   isForAllStaff: boolean,
 *   onRefresh?: () => void,
 *   onClearSelection?: () => void,
 * }} options
 */
export function useEventsWorkspaceDelete({
  t,
  selectedEvents,
  isForAllStaff,
  onRefresh,
  onClearSelection,
}) {
  const [confirmModal, setConfirmModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const isDeletingRef = useRef(false);

  const closeConfirmModal = useCallback(() => {
    if (isDeletingRef.current) return;
    setConfirmModal(null);
  }, []);

  const executeDelete = useCallback(
    async (items) => {
      if (!items?.length || isDeletingRef.current) return;

      isDeletingRef.current = true;
      setIsDeleting(true);
      try {
        const payloads = buildDeleteSchedulerEventsPayloads(items, {
          isForAllStaff,
        });
        if (!payloads.length) return;

        await Promise.all(
          payloads.map((payload) => deleteSchedulerEvents(payload)),
        );
        handleSuccess(t("scheduler.bulkDeleteSuccess"));
        setConfirmModal(null);
        onClearSelection?.();
        onRefresh?.();
      } catch (error) {
        handleError(error?.message || error);
      } finally {
        isDeletingRef.current = false;
        setIsDeleting(false);
      }
    },
    [isForAllStaff, t, onClearSelection, onRefresh],
  );

  const openDeleteSelectedConfirm = useCallback(() => {
    if (isDeletingRef.current) return;

    const items = Object.values(selectedEvents || {}).filter(
      (item) => item?.id != null && item?.date,
    );
    if (!items.length) return;

    const counts = getDeleteItemCounts(items);
    setConfirmModal({
      items,
      ...counts,
    });
  }, [selectedEvents]);

  const confirmDelete = useCallback(() => {
    if (!confirmModal?.items?.length || isDeletingRef.current) return;
    executeDelete(confirmModal.items);
  }, [confirmModal, executeDelete]);

  return {
    canDeleteSelected:
      Object.keys(selectedEvents || {}).length > 0 && !isDeleting,
    isDeleting,
    confirmModal,
    openDeleteSelectedConfirm,
    closeConfirmModal,
    confirmDelete,
  };
}
